/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { AfterViewInit, Component, ElementRef, Inject, OnDestroy } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialog as MatDialog,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { EMBEDS_CONTENT_GENERATOR_SERVICE, EmbedsContentGeneratorService } from '@app/app/editor/services/embeds-content-generator.service';
import { CoverImageUploadDialogComponent } from '@app/app/shared/components/cover-image-upload-dialog/cover-image-upload-dialog.component';
import { SpecialEmbedsHelper } from '@app/app/shared/helpers/embeds/special-embeds.helper';
import { FileUploadHelper } from '@app/app/shared/helpers/file-upload-helper';
import { LanguageCodeHelper } from '@app/app/shared/helpers/language-code-helper';
import { IMAGE_UPLOAD_DIALOG_CONFIG } from '@app/app/shared/models/config/dialog-config.model';
import { LucideCamera, LucideLink, LucideMic, LucidePaperclip, LucideSparkles, LucideVideo } from '@lucide/angular';
import { TranslocoService } from '@ngneat/transloco';
import { Select, Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { CreateMoment } from '../../../../../app-frame/store/app-frame.actions';
import {
  FileUploadDialogComponent,
  FileUploadDialogResult,
} from '../../../../../shared/components/file-upload-dialog/file-upload-dialog.component';
import { LinkDialogComponent } from '../../../../../shared/components/link-dialog/link-dialog.component';
import { DialogService } from '../../../../../shared/helpers/dialog/dialog.service';
import {
  EditorContent,
  EditorContentRemovalEvent,
  EditorContentUpdateEvent,
  MediaType,
  MediaUploadData,
  MediumEditorData,
  ResourceTag,
} from '../../../../../shared/models';
import { ExternalContentSummary } from '../../../../../shared/models/editor/media-content.model';
import { UserDetails } from '../../../../../user-auth/models';
import { UserAuthState } from '../../../../../user-auth/store/user-auth.state';
import { Moment, MomentVisibility } from '../../../models';
import { MomentCommonHelpers } from '../../../shared-moment-module/services/common.helpers';

export interface V3NewMomentDialogData {
  addToFolio: boolean;
  prefillContent?: string;
}

@Component({
  selector: 'ptl-v3-new-moment-dialog',
  templateUrl: './v3-new-moment-dialog.component.html',
  styleUrls: ['./v3-new-moment-dialog.component.scss'],
})
export class V3NewMomentDialogComponent implements AfterViewInit, OnDestroy {
  @Select(UserAuthState.userDetailsData)
  userDetailsData$: Observable<UserDetails>;

  protected readonly LucideSparkles = LucideSparkles;
  protected readonly LucidePaperclip = LucidePaperclip;
  protected readonly LucideCamera = LucideCamera;
  protected readonly LucideLink = LucideLink;
  protected readonly LucideVideo = LucideVideo;
  protected readonly LucideMic = LucideMic;

  moment: Moment = {
    title: '',
    tags: [],
    content: [],
    addedToFolio: false,
    likes: {
      totalLikes: 0,
      liked: false,
    },
    commentsCount: 0,
    totalViews: 0,
  };

  isPrivate = false;

  tagsPanelOpen = false;
  frameworkTagsPanelOpen = false;

  private dialogRefBackdropSubscription: Subscription;
  private dialogRefSubscription: Subscription;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: V3NewMomentDialogData,
    @Inject(EMBEDS_CONTENT_GENERATOR_SERVICE) private embedsService: EmbedsContentGeneratorService,
    private dialogRef: MatDialogRef<V3NewMomentDialogComponent>,
    private dialogService: DialogService,
    private store: Store,
    private translocoService: TranslocoService,
    private componentContainer: ElementRef,
    private dialog: MatDialog,
  ) {}

  ngAfterViewInit(): void {
    this.focusEditor();

    if (this.data?.prefillContent) {
      this.handlePrefillContent();
    }

    this.dialogRefBackdropSubscription = this.dialogRef.backdropClick().subscribe(() => {
      if (this.checkIfWasChanged()) {
        this.showConfirmMessage();
      } else {
        this.dialogRef.close();
      }
    });

    this.dialogRefSubscription = this.dialogRef.keydownEvents().subscribe((event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        if (this.checkIfWasChanged()) {
          this.showConfirmMessage();
        } else {
          this.dialogRef.close();
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.dialogRefBackdropSubscription?.unsubscribe();
    this.dialogRefSubscription?.unsubscribe();
  }

  onCancel(): void {
    if (this.checkIfWasChanged()) {
      this.showConfirmMessage();
    } else {
      this.dialogRef.close();
    }
  }

  onPost(): void {
    if (this.data?.addToFolio) {
      this.moment.addedToFolio = true;
    }

    this.moment.visibility = this.isPrivate ? MomentVisibility.Private : MomentVisibility.Public;

    this.store
      .dispatch(new CreateMoment(this.moment))
      .toPromise()
      .then(() => {
        setTimeout(() => {
          this.dialogRef.close(true);
        }, 0);
      });
  }

  onChange(momentChange: Moment): void {
    this.moment = momentChange;
  }

  onContentUpdate(contentUpdate: EditorContentUpdateEvent): void {
    const contentIndex = this.moment.content.findIndex((item) => item.uid === contentUpdate.update.oldContent.uid);
    if (contentIndex !== -1) {
      this.moment.content[contentIndex] = contentUpdate.update.newContent;
    } else {
      this.moment.content.push(contentUpdate.update.newContent);
    }
  }

  onContentRemoval(contentRemoval: EditorContentRemovalEvent): void {
    this.moment.content = this.moment.content.filter((item) => item.uid !== contentRemoval.content.uid);
  }

  onContentCreation(content: EditorContent): void {
    this.moment.content.push(content);
  }

  onAddTag(tag: ResourceTag): void {
    if (!this.moment.tags) {
      this.moment.tags = [];
    }

    const exists = this.moment.tags.some((existingTag) => existingTag.title === tag.title || existingTag.id === tag.id);
    if (!exists) {
      this.moment.tags.push(tag);
    }
  }

  onRemoveTag(tagIndex: number): void {
    if (this.moment.tags && tagIndex >= 0 && tagIndex < this.moment.tags.length) {
      this.moment.tags.splice(tagIndex, 1);
    }
  }

  onToggleTagsPanel(): void {
    this.tagsPanelOpen = !this.tagsPanelOpen;
  }

  onToggleFrameworkTagsPanel(value: boolean): void {
    this.frameworkTagsPanelOpen = value;
  }

  onAddTagBulk(tags: ResourceTag[]): void {
    for (const tag of tags) {
      this.onAddTag(tag);
    }
  }

  onAddPhoto(): void {
    const dialogRef = this.dialog.open(CoverImageUploadDialogComponent, {
      ...IMAGE_UPLOAD_DIALOG_CONFIG,
      direction: LanguageCodeHelper.getBodyLanguageDir(),
      data: {
        advancedImageCrop: true,
      },
    });

    dialogRef.afterClosed().subscribe((dialogResponse) => {
      if (dialogResponse && dialogResponse.croppedImageFile) {
        const file = dialogResponse.croppedImageFile;
        const imageContent = FileUploadHelper.convertFileToMediaUpload(file, 'IMAGE', false);
        this.moment.content.push(imageContent);
      }
    });
  }

  hasContent(): boolean {
    if (this.moment.title?.trim()) {
      return true;
    }

    if (this.moment.content && this.moment.content.length > 0) {
      return this.moment.content.some((content) => {
        if (content.type === 'PARAGRAPH' && content.content) {
          const trimmedContent = MomentCommonHelpers.textExtractedFrom(content.content);
          return trimmedContent.trim() !== '';
        }
        return content.type !== 'PARAGRAPH';
      });
    }

    return false;
  }

  changeDialogSizeMode(mode: string): void {
    if (mode === 'large') {
      this.expandDialog();
    } else {
      this.shrinkDialog();
    }
  }

  private expandDialog(): void {
    const dialogElement = document.querySelector('.v3-moment-dialog-container') as HTMLElement;
    const dialogContainerElement = document.querySelector('ptl-v3-new-moment-dialog') as HTMLElement;

    if (dialogElement) {
      dialogElement.style.width = '73.75rem';
      dialogElement.style.height = '80vh';
      dialogContainerElement.style.height = '100%';
    }
    this.focusEditor();
  }

  private shrinkDialog(): void {
    const dialogElement = document.querySelector('.v3-moment-dialog-container') as HTMLElement;
    const dialogContainerElement = document.querySelector('ptl-v3-new-moment-dialog') as HTMLElement;

    if (dialogElement) {
      dialogElement.style.width = '47rem';
      dialogElement.style.height = 'auto';
      dialogContainerElement.style.height = '100%';
    }
    this.focusEditor();
  }

  private focusEditor(): void {
    setTimeout(() => {
      let editorElement = this.componentContainer.nativeElement.querySelector('.medium-editor-element') as HTMLElement;
      if (!editorElement) {
        editorElement = this.componentContainer.nativeElement.querySelector('.mce-content-body') as HTMLElement;
      }
      if (editorElement) {
        editorElement.focus();
      }
    }, 300);
  }

  private showConfirmMessage(): void {
    this.dialogService
      .showConfirmDialog(this.translocoService.translate('translations.dialog.title.close'), this.translocoService)
      .then((confirmed) => {
        if (confirmed) {
          this.dialogRef.close();
        }
      });
  }

  private checkIfWasChanged(): boolean {
    const titleChanged = !!this.moment.title.trim();
    const tagsChanged = !!this.moment.tags.length;
    const contentChanged = this.checkIfContentWasChanged();
    return titleChanged || contentChanged || tagsChanged;
  }

  private checkIfContentWasChanged(): boolean {
    if (!this.moment.content.length) {
      return false;
    } else {
      let paragraphEmpty = true;
      let mediaEmpty = true;
      for (const contentItem of this.moment.content) {
        if (contentItem.type === 'PARAGRAPH') {
          const htmlContent = (contentItem as MediumEditorData).content;
          const trimmedString = MomentCommonHelpers.textExtractedFrom(htmlContent);
          paragraphEmpty = trimmedString.trim() === '';
        }
        if (contentItem.type === 'MEDIA_UPLOAD') {
          const file = (contentItem as MediaUploadData).file;
          if (file) {
            mediaEmpty = false;
          } else {
            mediaEmpty = true;
          }
        }
      }
      if (paragraphEmpty && mediaEmpty) {
        return false;
      } else {
        return true;
      }
    }
  }

  openFileUploadDialog(mediaType: MediaType): void {
    const dialogRef = this.dialog.open(FileUploadDialogComponent, {
      width: '47rem',
      height: '35rem',
      maxHeight: '80vh',
      position: { top: '10vh' },
      direction: LanguageCodeHelper.getBodyLanguageDir(),
      restoreFocus: true,
      panelClass: 'ptl-mat-dialog',
      backdropClass: 'dialog-backdrop',
      data: {
        type: mediaType,
      },
    });

    dialogRef.afterClosed().subscribe((result: FileUploadDialogResult | undefined) => {
      if (result?.file) {
        const mediaContent = FileUploadHelper.convertFileToMediaUpload(result.file, mediaType, false);
        this.moment.content.push(mediaContent);
      }
    });
  }

  onAddLink(): void {
    const dialogRef = this.dialog.open(LinkDialogComponent, {
      width: '36rem',
      position: { top: '30vh' },
      direction: LanguageCodeHelper.getBodyLanguageDir(),
      restoreFocus: true,
      panelClass: ['ptl-mat-dialog', 'add-link-editor-dialog'],
      backdropClass: 'dialog-backdrop',
    });

    dialogRef.afterClosed().subscribe((linkData: ExternalContentSummary) => {
      if (linkData) {
        this.moment.content.push(linkData);
      }
    });
  }

  private handlePrefillContent(): void {
    if (!this.data.prefillContent) return;

    const url = SpecialEmbedsHelper.correctUrl(this.data.prefillContent);

    this.embedsService
      .retrieveEmbeddableContentDetails(encodeURI(url))
      .pipe(take(1))
      .subscribe({
        next: ({ value }) => {
          if (value) {
            const content = {
              ...value,
              type: 'EXTERNAL_CONTENT_SUMMARY',
            } as unknown as ExternalContentSummary;

            this.moment.content.push(content);
          } else {
            this.createSimpleLink(url);
          }
        },
      });
  }

  private createSimpleLink(url: string): void {
    const linkContent: ExternalContentSummary = {
      uid: this.generateUid(),
      type: 'EXTERNAL_CONTENT_SUMMARY',
      url: url,
      authorName: '',
      logoUrl: '',
      title: this.translocoService.translate('translations.folio.moment.eventLink.title'),
      description: this.translocoService.translate('translations.folio.moment.eventLink.description'),
      publicationDate: new Date(),
      thumbnailUrl: '',
      siteName: 'Event',
      html: `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`,
      position: 'CENTER',
    };

    this.moment.content.push(linkContent);
  }

  private generateUid(): string {
    return 'link_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }
}
